import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AbstractControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { ConfirmDataApiRoaming } from '@api-roaming/models/confirm-data-api-roaming';
import { ConfirmEmailApiRoaming } from '@api-roaming/models/confirm-email-api-roaming';
import { RegEmailRequestApiRoaming } from '@api-roaming/models/reg-email-request-api-roaming';
import { RegistrationRequestApiRoaming } from '@api-roaming/models/registration-request-api-roaming';
import { ErrorResponse } from '@api/models';
import {
    RegisterConfirmFormField,
    RegisterConfirmFormService,
} from '@auth/components/register-confirm/register-confirm.form.service';
import { SystemChannelCode } from '@core/models/system-channel-code.model';
import { StartWithUnit } from '@core/operators/start-with-unit';
import { RouteEntity } from '@core/router/router.entity';
import { UserState } from '@core/state/user.state';
import { TranslateModule } from '@ngx-translate/core';
import { RxEffects } from '@rx-angular/state/effects';
import { FormErrorComponent } from '@shared/components/form/form-error/form-error.component';
import { SpinnerComponent } from '@shared/components/spinner/spinner.component';
import { RESEND_CODE_CONFIRM_TIMEOUT } from '@shared/constants';
import { LockupTimePipe, PhoneFormatPipe } from '@shared/pipes';
import { IMaskDirective } from 'angular-imask';
import { Observable, timer } from 'rxjs';
import { map, take } from 'rxjs/operators';


/**
 * Registration confirmation page
 */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-register-confirm',
    templateUrl: './register-confirm.component.html',
    styleUrls: ['./register-confirm.component.scss'],
    standalone: true,
    imports: [
        MatCardModule,
        AsyncPipe,
        ReactiveFormsModule,
        TranslateModule,
        PhoneFormatPipe,
        FormErrorComponent,
        MatFormFieldModule,
        IMaskDirective,
        MatInputModule,
        MatExpansionModule,
        LockupTimePipe,
        MatButtonModule,
        SpinnerComponent,
    ],
    providers: [RegisterConfirmFormService],
})
export class RegisterConfirmComponent {
    readonly #userState: UserState = inject(UserState);
    readonly #resendLockup: number = RESEND_CODE_CONFIRM_TIMEOUT;
    readonly #effects: RxEffects = inject(RxEffects);
    readonly #router: Router = inject(Router);
    readonly #registerConfirmFormService: RegisterConfirmFormService = inject(RegisterConfirmFormService);

    public readonly pending$: Observable<boolean> = this.#userState.loading$.pipe(
        map((loading: Record<StartWithUnit, boolean>) => loading?.[StartWithUnit.RegistrationConfirm]),
    );

    public readonly error$: Observable<ErrorResponse> = this.#userState.errors$.pipe(
        map((errors: Record<StartWithUnit, ErrorResponse>) => errors?.[StartWithUnit.RegistrationConfirm]),
    );

    public readonly registerData$: Observable<RegistrationRequestApiRoaming & RegEmailRequestApiRoaming> = this.#userState.registerData$;
    public registerData: RegistrationRequestApiRoaming & RegEmailRequestApiRoaming;

    public readonly form: FormGroup = this.#registerConfirmFormService.createReactiveForm();

    public readonly systemChannelCode: typeof SystemChannelCode = SystemChannelCode;
    public readonly registerConfirmFormField: typeof RegisterConfirmFormField = RegisterConfirmFormField;

    public lockUp$: Observable<number> | null;

    constructor() {
        this.lockupResend();

        this.#effects.register(
            this.registerData$,
            (reg: RegistrationRequestApiRoaming & RegEmailRequestApiRoaming) => {
                if (!reg) {
                    this.#router.navigate([RouteEntity.Root, RouteEntity.Register]);
                } else {
                    this.registerData = reg;
                }
            });
    }

    public get codeControl(): AbstractControl {
        return this.form ? this.form.get(RegisterConfirmFormField.Code) : undefined;
    }

    public confirmRegistration(): void {
        const data: ConfirmEmailApiRoaming & ConfirmDataApiRoaming = {
            email: this.registerData?.email || null,
            phone: this.registerData?.phone || null,
            code: this.codeControl?.value,
        };

        if (this.registerData.sysCode === 'email') {
            this.#userState.registrationConfirmByEmail(data);
        } else {
            this.#userState.registrationConfirmByPhone(data);
        }
    }

    public resendCode(): void {
        this.codeControl.setValue('');
        this.form.markAsUntouched();

        if (this.registerData.sysCode === 'email') {
            this.#userState.signInByEmail(this.registerData);
        } else {
            this.#userState.registrationByPhone(this.registerData);
        }

        // TODO: merge to a single lock-up component (shared with recover-confirm)
        this.lockupResend();
    }

    private lockupResend(): void {
        this.lockUp$ = timer(0, 1000).pipe(
            map((i: number) => this.#resendLockup - i),
            take(this.#resendLockup + 1),
        );
    }


}
